// API Client for Tesla Timeline Viewer v1.0.0
import type {
  TimelineSegment,
  VideoClip,
  CameraChannel,
  ClipEventType,
  MaskAuthorization,
  DetectionFrame,
} from '../types'

const API_BASE = import.meta.env.VITE_API_URL || 'https://tesla-ai-processor.bruteforce.workers.dev'

interface FetchOptions extends RequestInit {
  timeout?: number
}

async function fetchWithTimeout(url: string, options: FetchOptions = {}): Promise<Response> {
  const { timeout = 30000, ...fetchOptions } = options
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    })
    clearTimeout(id)
    return response
  } catch (error) {
    clearTimeout(id)
    throw error
  }
}

async function apiRequest<T>(endpoint: string, options?: FetchOptions): Promise<T> {
  const response = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(error.error || `API Error: ${response.status}`)
  }

  return response.json()
}

// ============== Timeline API ==============

export interface TimelineQuery {
  startDate: string // ISO date
  endDate: string   // ISO date
  channels?: CameraChannel[]
  eventTypes?: ClipEventType[]
  threatLevels?: string[]
  hasDetections?: boolean
}

export interface TimelineResponse {
  segments: TimelineSegment[]
  totalClips: number
  dateRange: {
    earliest: string
    latest: string
  }
  availableDates: string[] // Dates with footage
}

export async function fetchTimeline(query: TimelineQuery): Promise<TimelineResponse> {
  const params = new URLSearchParams({
    start: query.startDate,
    end: query.endDate,
  })
  if (query.channels?.length) params.set('channels', query.channels.join(','))
  if (query.eventTypes?.length) params.set('events', query.eventTypes.join(','))
  if (query.threatLevels?.length) params.set('threats', query.threatLevels.join(','))
  if (query.hasDetections) params.set('detections', 'true')

  return apiRequest<TimelineResponse>(`/viewer/timeline?${params}`)
}

// ============== Clip Streaming API ==============

export interface ClipStreamRequest {
  clipId: string
  channel: CameraChannel
  maskAuthorization?: string // Auth code for child mask bypass
}

export interface ClipStreamResponse {
  url: string
  expiresAt: string
  duration: number
  hasChildContent: boolean
  maskRegions?: Array<{
    startTime: number
    endTime: number
    box: { x: number; y: number; width: number; height: number }
  }>
}

export async function getClipStream(request: ClipStreamRequest): Promise<ClipStreamResponse> {
  const params = new URLSearchParams({
    id: request.clipId,
    channel: request.channel,
  })
  if (request.maskAuthorization) {
    params.set('auth', request.maskAuthorization)
  }

  return apiRequest<ClipStreamResponse>(`/viewer/stream?${params}`)
}

// Get clips at a specific time for all requested channels
export interface ClipsAtTimeRequest {
  time: string // ISO timestamp
  channels: CameraChannel[]
}

export interface ClipsAtTimeResponse {
  clips: Record<CameraChannel, VideoClip | null>
  nextClips: Record<CameraChannel, VideoClip | null>
  prevClips: Record<CameraChannel, VideoClip | null>
}

export async function getClipsAtTime(request: ClipsAtTimeRequest): Promise<ClipsAtTimeResponse> {
  return apiRequest<ClipsAtTimeResponse>('/viewer/clips-at-time', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

// ============== Detection Frames API ==============

export interface DetectionFramesRequest {
  clipId: string
  startTime?: number // Seconds into clip
  endTime?: number
}

export interface DetectionFramesResponse {
  frames: DetectionFrame[]
  childMaskRequired: boolean
}

export async function getDetectionFrames(request: DetectionFramesRequest): Promise<DetectionFramesResponse> {
  return apiRequest<DetectionFramesResponse>('/viewer/detections', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

// ============== Child Mask Authorization API ==============

export interface MaskAuthRequest {
  clipId: string
  reason: string
  requestedBy: string
}

export interface MaskAuthResponse {
  authorization: MaskAuthorization
  requiresApproval: boolean
}

export async function requestMaskAuthorization(request: MaskAuthRequest): Promise<MaskAuthResponse> {
  return apiRequest<MaskAuthResponse>('/viewer/mask-auth/request', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function verifyMaskAuthorization(authCode: string): Promise<MaskAuthorization | null> {
  try {
    return await apiRequest<MaskAuthorization>(`/viewer/mask-auth/verify?code=${authCode}`)
  } catch {
    return null
  }
}

export async function revokeMaskAuthorization(authId: string): Promise<void> {
  await apiRequest('/viewer/mask-auth/revoke', {
    method: 'POST',
    body: JSON.stringify({ id: authId }),
  })
}

// ============== Thumbnail API ==============

export async function getThumbnailUrl(clipId: string, time?: number): Promise<string> {
  const params = new URLSearchParams({ id: clipId })
  if (time !== undefined) params.set('t', time.toString())
  const response = await apiRequest<{ url: string }>(`/viewer/thumbnail?${params}`)
  return response.url
}

// ============== Export API ==============

export interface ExportRequest {
  startTime: string
  endTime: string
  channels: CameraChannel[]
  includeDetections: boolean
  removeMasks: boolean
  maskAuthCode?: string
}

export interface ExportResponse {
  jobId: string
  estimatedSize: number
  estimatedDuration: number
}

export async function startExport(request: ExportRequest): Promise<ExportResponse> {
  return apiRequest<ExportResponse>('/viewer/export/start', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export interface ExportStatus {
  jobId: string
  status: 'pending' | 'processing' | 'complete' | 'failed'
  progress: number
  downloadUrl?: string
  error?: string
}

export async function getExportStatus(jobId: string): Promise<ExportStatus> {
  return apiRequest<ExportStatus>(`/viewer/export/status?id=${jobId}`)
}
